import React, { useEffect, useState } from 'react';
import { Invoice, listInvoices, sendInvoice, resendInvoice, voidInvoice } from '../services/invoiceService';
import CreateInvoiceModal from './CreateInvoiceModal';
import { buildSymbolMap, currencySymbol, FALLBACK_SYMBOL_MAP, SymbolMap } from '../utils/currency';
import { config } from '../config/environment';

const STATUS_BADGE: Record<string, string> = {
  draft:   'bg-gray-800 text-gray-300 border-dark-border',
  sent:    'bg-blue-900/40 text-blue-300 border-blue-800/50',
  viewed:  'bg-purple-900/40 text-purple-300 border-purple-800/50',
  paid:    'bg-green-900/40 text-green-300 border-green-800/50',
  overdue: 'bg-red-900/40 text-red-300 border-red-800/50',
  void:    'bg-gray-700 text-gray-500 border-gray-700',
};

interface Props {
  clients: { client_id: string; client_name: string }[];
}

const InvoicesTab: React.FC<Props> = ({ clients }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [symbolMap, setSymbolMap] = useState<SymbolMap>(FALLBACK_SYMBOL_MAP);

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then((countries: any[]) => setSymbolMap(buildSymbolMap(countries)))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listInvoices({
        status: statusFilter || undefined,
        client_id: clientFilter || undefined,
      });
      setInvoices(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, clientFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async (inv: Invoice) => {
    setActionLoading(inv.id);
    setError('');
    try {
      const updated = await sendInvoice(inv.id);
      setInvoices(prev => prev.map(i => i.id === updated.id ? updated : i));
    } catch (e: any) { setError(e.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleResend = async (inv: Invoice) => {
    setActionLoading(inv.id);
    setError('');
    try {
      const updated = await resendInvoice(inv.id);
      setInvoices(prev => prev.map(i => i.id === updated.id ? updated : i));
    } catch (e: any) { setError(e.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleVoid = async (inv: Invoice) => {
    if (!window.confirm(`Void invoice ${inv.invoice_number}?`)) return;
    setActionLoading(inv.id);
    setError('');
    try {
      const updated = await voidInvoice(inv.id);
      setInvoices(prev => prev.map(i => i.id === updated.id ? updated : i));
    } catch (e: any) { setError(e.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <div className="space-y-5">
      {showCreateModal && (
        <CreateInvoiceModal
          clients={clients}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); load(); }}
        />
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 focus:outline-none focus:border-primary-500"
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="void">Void</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Client</label>
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            className="px-3 py-1.5 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 focus:outline-none focus:border-primary-500"
          >
            <option value="">All</option>
            {clients.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-dark-border rounded-lg transition-colors"
        >
          Refresh
        </button>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex-shrink-0"
        >
          + Create Invoice
        </button>
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-500">No invoices found. Create one from a session in Schedules.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-dark-border">
                <th className="pb-2 text-left font-medium">Invoice</th>
                <th className="pb-2 text-left font-medium">Client</th>
                <th className="pb-2 text-left font-medium">Engagement Type</th>
                <th className="pb-2 text-left font-medium">Name</th>
                <th className="pb-2 text-left font-medium">Session Date</th>
                <th className="pb-2 text-right font-medium pr-8">Amount</th>
                <th className="pb-2 text-left font-medium pr-6">Status</th>
                <th className="pb-2 text-left font-medium pr-4">Due Date</th>
                <th className="pb-2 text-left font-medium">Date Paid</th>
                <th className="pb-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-border">
              {invoices.map(inv => {
                const busy = actionLoading === inv.id;
                const dueStr = inv.due_date
                  ? new Date(inv.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—';
                const paidStr = inv.paid_at
                  ? new Date(inv.paid_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                  : null;
                const sessionStr = inv.session_date
                  ? new Date(inv.session_date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                  : '—';
                const isPackage = !!inv.package_payment_id;
                const engagementType = isPackage ? 'Package' : 'Session';
                const engagementName = isPackage
                  ? (inv.payment_type === 'installment' && inv.installment_number != null
                      ? `${inv.package_name ?? 'Package'} — Installment ${inv.installment_number}`
                      : (inv.package_name ?? 'Package'))
                  : (inv.meeting_name ?? inv.description ?? '—');
                return (
                  <tr key={inv.id} className="text-gray-300 hover:bg-dark-hover/30">
                    <td className="py-2.5 pr-4 font-medium">
                      <a
                        href={inv.payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 hover:underline"
                      >
                        {inv.invoice_number}
                      </a>
                    </td>
                    <td className="py-2.5 pr-4">{inv.client_name}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded border font-medium ${
                        isPackage
                          ? 'bg-purple-900/30 text-purple-300 border-purple-800/50'
                          : 'bg-blue-900/30 text-blue-300 border-blue-800/50'
                      }`}>
                        {engagementType}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-300 max-w-[200px] truncate" title={engagementName}>{engagementName}</td>
                    <td className="py-2.5 pr-4 text-gray-400">{isPackage ? '—' : sessionStr}</td>
                    <td className="py-2.5 pr-8 text-right font-medium">{currencySymbol(inv.currency, symbolMap)}{(inv.amount_cents / 100).toFixed(2)}</td>
                    <td className="py-2.5 pr-6">
                      {inv.payment_status === 'failed' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full border bg-red-900/40 text-red-300 border-red-800/50">
                          Failed
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_BADGE[inv.status] ?? STATUS_BADGE.draft}`}>
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-400">{dueStr}</td>
                    <td className="py-2.5 pr-4 text-gray-400">
                      {paidStr ? <span className="text-green-400">{paidStr}</span> : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="py-2.5">
                      <div className="flex gap-1.5 flex-wrap">
                        {inv.status === 'draft' && (
                          <button
                            disabled={busy}
                            onClick={() => handleSend(inv)}
                            className="px-2 py-1 text-xs bg-blue-900/40 text-blue-300 border border-blue-800/50 rounded hover:bg-blue-800/60 transition-colors disabled:opacity-50"
                          >
                            {busy ? '…' : 'Send'}
                          </button>
                        )}
                        {(inv.status === 'sent' || inv.status === 'viewed' || inv.status === 'overdue') && (
                          <button
                            disabled={busy}
                            onClick={() => handleResend(inv)}
                            className="px-2 py-1 text-xs bg-blue-900/40 text-blue-300 border border-blue-800/50 rounded hover:bg-blue-800/60 transition-colors disabled:opacity-50"
                          >
                            {busy ? '…' : 'Resend'}
                          </button>
                        )}
                        {inv.status !== 'paid' && inv.status !== 'void' && (
                          <>
                            <button
                              onClick={() => copyLink(inv.payment_url)}
                              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-300 border border-dark-border rounded transition-colors"
                              title="Copy payment link"
                            >
                              Copy link
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => handleVoid(inv)}
                              className="px-2 py-1 text-xs text-gray-600 hover:text-red-400 border border-dark-border rounded transition-colors disabled:opacity-50"
                            >
                              {busy ? '…' : 'Void'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoicesTab;
